import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { endpoints, type EntityListItem, type ListEntitiesParams } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui/Table";
import { Badge } from "@/components/ui/Badge";
import { fmtRelativeDays } from "@/lib/format";
import { CreateEntityInline } from "@/components/EntityPicker";

// Entities — v2.8.0 Day 4 — cross-platform identity list.
// Each row = one entity (an abstract real-world person/brand/MCN) that
// owns 1..N channels across platforms. Day 1's strict 1:1 backfill means
// every channel currently has its own entity; this list will get
// shorter as operators use Day 6's "move to entity" UI to merge.
//
// `is_internal` filter ("我们的") surfaces the Bitbase-self sub-product
// in isolation (Day 7 lands a dedicated /entities/self route).
//
// Page size 50 is the same cap as the API; operators rarely scroll deep
// — search by canonical_name or primary_handle is the intended path.

const PAGE_SIZE = 50;

type InternalFilter = "all" | "internal" | "external";

// initialFilterFromSearch translates the ?is_internal= URL param into
// the segmented-control state. Used so /entities?is_internal=true links
// (e.g. from the EntitySelf 409 Conflict screen) land on the right tab.
function initialFilterFromSearch(params: URLSearchParams): InternalFilter {
  const v = params.get("is_internal");
  if (v === "true" || v === "1") return "internal";
  if (v === "false" || v === "0") return "external";
  return "all";
}

export default function Entities() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchDraft, setSearchDraft] = useState(searchParams.get("search") ?? "");
  const [search, setSearch] = useState(searchParams.get("search") ?? "");
  const [internalFilter, setInternalFilter] = useState<InternalFilter>(
    initialFilterFromSearch(searchParams),
  );
  const [offset, setOffset] = useState(0);
  const [showCreate, setShowCreate] = useState(false);

  // Sync filter state → URL so the choice is shareable / back-button
  // friendly. Replace (not push) so each filter tweak doesn't pile up
  // history entries.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (search) next.set("search", search);
    else next.delete("search");
    if (internalFilter === "internal") next.set("is_internal", "true");
    else if (internalFilter === "external") next.set("is_internal", "false");
    else next.delete("is_internal");
    // Only setSearchParams if the string actually changed; otherwise
    // RR would keep firing.
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, internalFilter]);

  // 300ms debounce so each keystroke doesn't fire a request. Reset offset
  // whenever the filter changes (otherwise page 3 of "sheldon" becomes
  // page 3 of "bitbase" on filter switch — confusing).
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchDraft.trim());
      setOffset(0);
    }, 300);
    return () => clearTimeout(t);
  }, [searchDraft]);
  useEffect(() => {
    setOffset(0);
  }, [internalFilter]);

  const params: ListEntitiesParams = {
    limit: PAGE_SIZE,
    offset,
  };
  if (search) params.search = search;
  if (internalFilter === "internal") params.is_internal = true;
  if (internalFilter === "external") params.is_internal = false;

  const listQ = useQuery({
    queryKey: ["entities", "list", search, internalFilter, offset],
    queryFn: () => endpoints.listEntities(params),
    refetchOnWindowFocus: false,
  });

  const items = listQ.data?.items ?? [];
  const total = listQ.data?.total ?? 0;
  const hasPrev = offset > 0;
  const hasNext = offset + items.length < total;
  const pageStart = items.length === 0 ? 0 : offset + 1;
  const pageEnd = offset + items.length;

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-border bg-background px-6 py-3">
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-semibold">Entities</h1>
          <span className="text-sm text-muted-foreground">
            共 {total} 个实体 · 跨平台身份层(v2.8.0)
          </span>
          <Button
            size="sm"
            className="ml-auto"
            onClick={() => setShowCreate(true)}
          >
            + 新建实体
          </Button>
        </div>
        <div className="mt-3 flex items-center gap-3">
          <div className="flex items-center gap-1.5 rounded-md border border-border bg-muted/40 p-0.5 text-xs">
            <FilterPill
              active={internalFilter === "all"}
              onClick={() => setInternalFilter("all")}
            >
              全部
            </FilterPill>
            <FilterPill
              active={internalFilter === "internal"}
              onClick={() => setInternalFilter("internal")}
            >
              ★ 我们的
            </FilterPill>
            <FilterPill
              active={internalFilter === "external"}
              onClick={() => setInternalFilter("external")}
            >
              KOL / 外部
            </FilterPill>
          </div>
          <Input
            placeholder="按名称或 handle 搜索…"
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            className="max-w-xs"
          />
          {(search || internalFilter !== "all") && (
            <button
              type="button"
              onClick={() => {
                setSearchDraft("");
                setInternalFilter("all");
              }}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              清空筛选
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-auto bg-background">
        {listQ.isLoading && (
          <p className="p-4 text-sm text-muted-foreground">加载中…</p>
        )}
        {listQ.isError && (
          <p className="p-4 text-sm text-destructive">
            加载失败:{(listQ.error as Error).message}
          </p>
        )}
        {!listQ.isLoading && !listQ.isError && items.length === 0 && (
          <p className="p-6 text-sm text-muted-foreground">
            {search || internalFilter !== "all"
              ? "没有匹配的实体。试着调整筛选条件。"
              : "还没有实体。新增频道会自动建一个 1:1 entity。"}
          </p>
        )}
        {items.length > 0 && (
          <Table>
            <THead>
              <TR>
                <TH>名称</TH>
                <TH>Handle</TH>
                <TH className="text-right">Channels</TH>
                <TH>类型</TH>
                <TH className="text-right">创建于</TH>
              </TR>
            </THead>
            <TBody>
              {items.map((e) => (
                <EntityRow key={e.id} entity={e} />
              ))}
            </TBody>
          </Table>
        )}
      </div>

      {showCreate && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowCreate(false);
          }}
        >
          <div className="w-full max-w-lg rounded-lg border border-border bg-background p-4 shadow-xl">
            <h2 className="mb-3 text-base font-semibold">新建 entity</h2>
            <CreateEntityInline
              defaultName=""
              onCancel={() => setShowCreate(false)}
              onCreated={(entity) => {
                setShowCreate(false);
                // Jump straight to the detail page so the operator can
                // immediately move channels in via the picker. List query
                // gets invalidated inside CreateEntityInline already.
                navigate(`/entities/${entity.id}`);
              }}
            />
          </div>
        </div>
      )}

      {/* Pagination footer — only renders when there's something to page */}
      {(hasPrev || hasNext) && (
        <div className="flex items-center justify-between border-t border-border bg-background px-6 py-2 text-xs text-muted-foreground">
          <span>
            显示 {pageStart}-{pageEnd} · 共 {total}
          </span>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="ghost"
              disabled={!hasPrev}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              上一页
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={!hasNext}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              下一页
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// EntityRow — one line in the table. The "类型" column derives a coarse
// label from is_internal + channels_count, per entities-design.md §A note:
//   - is_internal=true              → "★ 我们"
//   - channels_count >= 2           → "MCN/集合"  (could refine when handle
//                                      pattern signals MCN, deferred to
//                                      Day 7+)
//   - default                       → "—"
function EntityRow({ entity }: { entity: EntityListItem }) {
  return (
    <TR>
      <TD className="font-medium">
        <Link
          to={`/entities/${entity.id}`}
          className="text-primary hover:underline"
        >
          {entity.canonical_name}
        </Link>
      </TD>
      <TD className="font-mono text-xs text-muted-foreground">
        {entity.primary_handle ?? "—"}
      </TD>
      <TD className="text-right tabular-nums">{entity.channels_count}</TD>
      <TD>
        {entity.is_internal ? (
          <Badge className="bg-amber-100 text-amber-700">★ 我们</Badge>
        ) : entity.channels_count >= 2 ? (
          <Badge variant="secondary">集合</Badge>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </TD>
      <TD className="text-right text-xs text-muted-foreground">
        {fmtRelativeDays(entity.created_at)}
      </TD>
    </TR>
  );
}

// FilterPill — small segmented-control pill. Inline rather than its own
// component file because it's used only here (so far); promote to
// components/ui/ when a second consumer appears.
function FilterPill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        active
          ? "rounded bg-background px-2.5 py-1 font-medium text-foreground shadow-sm"
          : "rounded px-2.5 py-1 text-muted-foreground hover:text-foreground"
      }
    >
      {children}
    </button>
  );
}
